// Sistema de tratamento de erros para protótipo
export class AppError extends Error {
  constructor(
    message: string,
    public code: string = 'UNKNOWN_ERROR',
    public severity: 'low' | 'medium' | 'high' = 'medium'
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export const errorMessages = {
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet.',
  TIMEOUT_ERROR: 'Operação demorou muito para responder. Tente novamente.',
  VALIDATION_ERROR: 'Dados inválidos. Verifique os campos.',
  AUTH_ERROR: 'Erro de autenticação. Faça login novamente.',
  NOT_FOUND: 'Recurso não encontrado.',
  SERVER_ERROR: 'Erro interno do servidor. Tente novamente mais tarde.',
  PERMISSION_DENIED: 'Você não tem permissão para esta ação.',
  DUPLICATE_ERROR: 'Este registro já existe.',
  REQUIRED_FIELD: 'Este campo é obrigatório.',
  INVALID_FORMAT: 'Formato inválido.',
} as const;

export const handleError = (error: unknown): { message: string; code: string; severity: 'low' | 'medium' | 'high' } => {
  // Log do erro para debugging (em produção seria enviado para serviço de monitoramento)

  
  if (error instanceof AppError) {
    return {
      message: error.message,
      code: error.code,
      severity: error.severity
    };
  }
  
  if (error instanceof Error) {
    // Mapeia erros comuns
    if (error.message.includes('timeout')) {
      return {
        message: errorMessages.TIMEOUT_ERROR,
        code: 'TIMEOUT_ERROR',
        severity: 'medium'
      };
    }
    
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return {
        message: errorMessages.NETWORK_ERROR,
        code: 'NETWORK_ERROR',
        severity: 'high'
      };
    }
    
    return {
      message: error.message || errorMessages.SERVER_ERROR,
      code: 'UNKNOWN_ERROR',
      severity: 'medium'
    };
  }
  
  return {
    message: errorMessages.SERVER_ERROR,
    code: 'UNKNOWN_ERROR',
    severity: 'high'
  };
};

// Simulador de operações assíncronas com tratamento de erro
export const simulateAsyncOperation = async <T>(
  operation: () => Promise<T> | T,
  options: {
    delay?: number;
    failureRate?: number; // 0-1 (0 = nunca falha, 1 = sempre falha)
    timeoutMs?: number;
  } = {}
): Promise<T> => {
  const { delay = 800, failureRate = 0, timeoutMs = 5000 } = options;
  
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new AppError(errorMessages.TIMEOUT_ERROR, 'TIMEOUT_ERROR', 'high'));
    }, timeoutMs);
    
    setTimeout(async () => {
      try {
        // Simula falha baseada na taxa de falha
        if (Math.random() < failureRate) {
          throw new AppError(errorMessages.SERVER_ERROR, 'SIMULATED_ERROR', 'medium');
        }
        
        const result = await operation();
        clearTimeout(timeoutId);
        resolve(result);
      } catch (error) {
        clearTimeout(timeoutId);
        reject(error);
      }
    }, delay);
  });
};

// Helper para retry de operações
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> => {
  let lastError: unknown;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
    }
  }
  
  throw lastError;
};